import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Confirm from '@hobs/components/Confirm';
import Toast from '@hobs/components/Toast';
import { H2 } from '@hobs/components/Headings';
import { Tree } from '@blueprintjs/core';

import { getPartyUnits } from '../../actions/parties';

import { deleteUnit } from '../../actions/units';
import UnitDialog from './UnitDialog';
import './Units.scss';

class Units extends React.PureComponent {
  getUnitPathName(unitId = '') {
    return `${this.props.location.pathname.replace(
      '/unit',
      ''
    )}/unit/${unitId}`;
  }

  onRemove(unitId) {
    Confirm(
      this.props.t('Delete unit'),
      this.props.t('Are you sure you want to delete this unit?'),
      () => {
        const { t } = this.props;
        this.props.deleteUnit(unitId).then(response => {
          if (response.success) {
            Toast.success(t('Unit deleted successfully'));
            this.reloadUnits();
          } else {
            Toast.warning(response.payload.message);
          }
        });
      }
    );
  }

  getUnitDeletePathName(unitId) {
    return `${this.props.location.pathname.replace(
      '/deleteunit',
      ''
    )}/deleteunit/${unitId}`;
  }

  controls(unit) {

    return (
      this.props.canEdit && (
        <span className="units-tree--controls">
          <GhostButton
            iconLeft={<Icon icon="pen" />}
            to={this.getUnitPathName(unit.unitId)}
          >
            {this.props.t('Edit')}
          </GhostButton>
          <GhostButton
            iconLeft={<Icon icon="delete" />}
            onClick={() => {
              this.onRemove(unit.unitId);
            }}
          >
            {this.props.t('Delete')}
          </GhostButton>
        </span>
      )
    );
  }

  buildTree = (fn, [head, ...rest]) => {
    if (head === undefined) {
      return [];
    }
    if (head.units.length) {
      return [
        { ...fn(head), childNodes: this.buildTree(fn, head.units) },
        ...this.buildTree(fn, rest)
      ];
    }
    return [fn(head), ...this.buildTree(fn, rest)];
  };

  getUnitObject = unit => {
    return {
      ...unit,
      isExpanded: true,
      hasCaret: false,
      secondaryLabel: this.controls(unit),
      id: unit.unitId,
      key: unit.unitId,
      label: unit.title
    };
  };

  reloadUnits = () => {
    this.props.getPartyUnits(this.props.partyId);
  };

  sortUnits(units) {
    if (!Array.isArray(units)) {
      return [];
    }

    const sortedUnits = units.sort((a, b) =>
      a.title.toLowerCase() > b.title.toLowerCase()
        ? 1
        : a.title.toLowerCase() < b.title.toLowerCase()
        ? -1
        : 0
    );

    return sortedUnits.map(unit => {
      return {
        ...unit,
        units: this.sortUnits(unit.units)
      };
    });
  }

  render() {
    const { t, partyId, partyUnits } = this.props;
    const sortedUnits = this.sortUnits(partyUnits);
    const unitsTree = this.buildTree(
      this.getUnitObject,
      this.props.partyUnits ? sortedUnits : []
    );

    return (
      <div className="units-view">
        <H2>{t('Manage structure')}</H2>
        <UnitDialog
          partyId={partyId}
          units={sortedUnits}
          reloadUnits={this.reloadUnits}
        />

        <div>
          <div className="units-view--header">
            <div className="units-view--left">
              <div>
                {this.props.canEdit && (
                  <RaisedButton
                    iconLeft={<Icon icon="plus" />}
                    to={this.getUnitPathName()}
                  >
                    {t('Add new element')}
                  </RaisedButton>
                )}
              </div>
            </div>
          </div>
          <div>
            {unitsTree.length > 0 && (
              <Tree
                className="units-tree"
                contents={unitsTree}
                onNodeCollapse={this.handleNodeCollapse}
                onNodeExpand={this.handleNodeExpand}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

Units.propTypes = {
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  t: PropTypes.func,
  partyUnits: PropTypes.array,
  partyId: PropTypes.string,
  getPartyUnits: PropTypes.func,
  deleteUnit: PropTypes.func,
  canEdit: PropTypes.bool
};

const mapStateToProps = state => ({
  partyUnits: state.parties.partyUnits,
  deleteUnit: PropTypes.func
});

const mapDispatchToProps = {
  getPartyUnits,
  deleteUnit
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Units));
